import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequireFeature } from '@/features/authentication';
import { LazyLoad } from '@/shared/components/LazyLoad';
import { PURCHASE_REQUESTS_CHANNEL } from './purchase-request/models/channels';
import { FormsChannelContext } from './purchase-request/hooks/formsChannelContext';

const SubmittedFormPage = lazy(() => import('./purchase-request/pages/SubmittedFormPage'));
const PurchaseRequestsPage = lazy(() => import('./purchase-request/pages/PurchaseRequestsPage'));
const NewPurchaseRequestPage = lazy(() => import('./purchase-request/pages/NewPurchaseRequestPage'));

/**
 * Purchase Requests (NB-355): the forms pages for PR Templates, behind the `Purchase Requests` grants --
 * the requests and My Approvals (VIEW), New Purchase Request with its hierarchy dropdowns and active PR Template
 * cards (CREATE), and one request with its approver's decision (VIEW). Each page reads the Purchase Requests
 * channel: its `/purchase-requests` API, its titles, its columns.
 */
const PR_FEATURE = PURCHASE_REQUESTS_CHANNEL.feature;

export const purchaseRequestRoutes: RouteObject[] = [
  {
    path: 'purchase-requests',
    element: (
      <RequireFeature feature={PR_FEATURE}>
        <FormsChannelContext.Provider value={PURCHASE_REQUESTS_CHANNEL}>
          <LazyLoad>
            <PurchaseRequestsPage />
          </LazyLoad>
        </FormsChannelContext.Provider>
      </RequireFeature>
    ),
  },
  {
    path: 'purchase-requests/new',
    element: (
      <RequireFeature feature={PR_FEATURE} action="create">
        <FormsChannelContext.Provider value={PURCHASE_REQUESTS_CHANNEL}>
          <LazyLoad>
            <NewPurchaseRequestPage />
          </LazyLoad>
        </FormsChannelContext.Provider>
      </RequireFeature>
    ),
  },
  /* Edit (UPDATE): the request's template with its answers, saved over it. Duplicate (CREATE): the same, as a new one. */
  {
    path: 'purchase-requests/:id/edit',
    element: (
      <RequireFeature feature={PR_FEATURE} action="update">
        <FormsChannelContext.Provider value={PURCHASE_REQUESTS_CHANNEL}>
          <LazyLoad>
            <NewPurchaseRequestPage mode="edit" />
          </LazyLoad>
        </FormsChannelContext.Provider>
      </RequireFeature>
    ),
  },
  {
    path: 'purchase-requests/:id/duplicate',
    element: (
      <RequireFeature feature={PR_FEATURE} action="create">
        <FormsChannelContext.Provider value={PURCHASE_REQUESTS_CHANNEL}>
          <LazyLoad>
            <NewPurchaseRequestPage mode="duplicate" />
          </LazyLoad>
        </FormsChannelContext.Provider>
      </RequireFeature>
    ),
  },
  {
    path: 'purchase-requests/:id',
    element: (
      <RequireFeature feature={PR_FEATURE}>
        <FormsChannelContext.Provider value={PURCHASE_REQUESTS_CHANNEL}>
          <LazyLoad>
            <SubmittedFormPage />
          </LazyLoad>
        </FormsChannelContext.Provider>
      </RequireFeature>
    ),
  },
];
